// 線上補查（內建字典查不到時才用）：
//   翻譯：MyMemory（免費、免 key，匿名每天約 5000 字元）→ 繁體中文，屬機器翻譯
//   英文釋義：Free Dictionary API → 音標、發音、英文解釋
// 只送出使用者輸入的單字／短句，不含帳號資訊
export const MAX_ONLINE_CHARS = 100;

export type Translation = { main: string; alts: string[] };
export type Definition = { phonetic?: string; audio?: string; meanings: { pos: string; defs: string[] }[] };
export class QuotaError extends Error {}

const cache = new Map<string, Promise<unknown>>();
const memo = <T,>(key: string, run: () => Promise<T>): Promise<T> => {
  if (!cache.has(key)) cache.set(key, run().catch(e => { cache.delete(key); throw e; }));
  return cache.get(key) as Promise<T>;
};
const hasCjk = (s: string) => /[㐀-鿿]/.test(s);

// dir：'zh' 英翻中、'en' 中翻英
export function translate(text: string, dir: 'zh' | 'en' = 'zh'): Promise<Translation> {
  const q = text.trim();
  const pair = dir === 'zh' ? 'en|zh-TW' : 'zh-TW|en';
  const good = (t: string) => (dir === 'zh' ? hasCjk(t) : /[A-Za-z]/.test(t) && !hasCjk(t));
  return memo(`t${dir}:${q}`, async () => {
    const r = await fetch(`https://api.mymemory.translated.net/get?q=${encodeURIComponent(q)}&langpair=${pair}`);
    if (r.status === 429) throw new QuotaError('quota');
    if (!r.ok) throw new Error(`translate ${r.status}`);
    const d = await r.json();
    const main: string = (d?.responseData?.translatedText ?? '').trim();
    if (d?.quotaFinished || /MYMEMORY WARNING/i.test(main)) throw new QuotaError('quota');
    if (!good(main)) throw new Error('no translation');
    const alts: string[] = [];
    // 單字（英文沒有空白、中文很短）才列出其他翻法；句子的其他比對多半是不相干的句子
    const short = dir === 'zh' ? !q.includes(' ') : q.length <= 6;
    if (short) {
      for (const m of (d.matches ?? []) as { translation?: string }[]) {
        const t = (m.translation ?? '').trim();
        if (t && good(t) && t.length <= 30 && t.toLowerCase() !== main.toLowerCase() && !alts.some(a => a.toLowerCase() === t.toLowerCase())) alts.push(t);
        if (alts.length >= 2) break;
      }
    }
    return { main, alts };
  });
}

export function define(word: string): Promise<Definition | null> {
  const w = word.trim().toLowerCase();
  return memo(`d:${w}`, async () => {
    const r = await fetch(`https://api.dictionaryapi.dev/api/v2/entries/en/${encodeURIComponent(w)}`);
    if (r.status === 404) return null;
    if (!r.ok) throw new Error(`define ${r.status}`);
    const d = await r.json();
    const first = Array.isArray(d) ? d[0] : null;
    if (!first) return null;
    const ph = (first.phonetics ?? []) as { text?: string; audio?: string }[];
    const meanings = ((first.meanings ?? []) as { partOfSpeech: string; definitions: { definition: string }[] }[])
      .slice(0, 2).map(m => ({ pos: m.partOfSpeech, defs: m.definitions.slice(0, 2).map(x => x.definition) }));
    return {
      phonetic: first.phonetic || ph.find(p => p.text)?.text,
      audio: ph.find(p => p.audio)?.audio || undefined,
      meanings,
    };
  });
}
